<template>
    <div :class="props.fluid ? 'flex w-full' : 'flex flex-column align-items-center'">
        <!-- Start Scan Button -->
        <Button
            class="white-space-nowrap flex align-items-center justify-content-center gap-2"
            :class="props.fluid
                ? (props.outlined ? 'w-full queue-action-btn p-button-outlined' : 'w-full queue-action-btn')
                : 'text-xl flex-auto sm:flex-initial'"
            @click="openScanDialog">
            <i class="pi pi-qrcode"></i>
            <span>Scan</span>
        </Button>

        <!-- Camera Dialog -->
        <Dialog v-model:visible="scanDialogVisible" header="Scan QR Code" modal :closable="false" class="p-dialog-sm">
            <div class="dialog-wrapper">
                <Card class="border-none shadow-none flex flex-column justify-content-between h-full w-full">
                    <template #content>
                        <div class="flex flex-column gap-2 align-items-center justify-content-center qr-camera">
                            <!-- Skeleton loader while scanning -->
                            <div class="loading-wrapper">
                                <Skeleton v-if="cameraLoading" width="100%" height="100%" borderRadius="8px" />
                                <!-- QR Code Scanner -->
                                <qrcode-stream v-show="!cameraLoading" :paused="paused" @detect="handleDetection"
                                    @error="handleCameraError" @camera-on="cameraLoaded" />
                                <div v-if="cameraLoading" class="loading-indicator">Loading...</div>
                            </div>
                        </div>
                    </template>
                </Card>
            </div>
            <template #footer>
                <div class="flex justify-content-center mt-3">
                    <Button label="Cancel" class="p-button-outline" @click="closeScanDialog(false)" />
                </div>
            </template>
        </Dialog>

        <!-- Result Dialog -->
        <Dialog v-model:visible="resultDialogVisible" header="Scanned QR Code" modal :closable="false"
            class="p-dialog-sm">
            <div class="dialog-wrapper">
                <Card class="border-none shadow-none flex flex-column justify-content-between h-full w-full">
                    <template #content>
                        <div class="flex flex-column gap-2 justify-content-center qr-result">
                            <div v-for="(val, key) in scannedData" :key="key" class="flex justify-content-between">
                                <span class="text-900">{{ key }}: </span><span>{{ val }}</span>
                            </div>
                        </div>
                    </template>
                </Card>
            </div>
            <template #footer>
                <div class="flex justify-content-between w-full mt-3 px-3">
                    <Button label="Cancel" class="p-button-outlined" @click="closeScanDialog" />
                    <Button label="Submit" class="p-button-success" @click="submitResult" />
                </div>
            </template>
        </Dialog>
    </div>
</template>

<script setup>
import { ref, defineProps } from 'vue'
import { QrcodeStream } from 'vue-qrcode-reader'
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import Card from 'primevue/card'
import Skeleton from 'primevue/skeleton'
import Toast from 'primevue/toast'

// Define the props
const props = defineProps({
    autoSubmit: {
        type: Boolean,
        default: true
    },
    template: {
        type: String,
        default: ''
    },
    formExtraData: {
        type: Object,
        default: () => { }
    },
    // When true the scan button fills its container (compact rows / action bar).
    fluid: {
        type: Boolean,
        default: false,
    },
    // When true (with fluid) the scan button is outlined instead of solid.
    outlined: {
        type: Boolean,
        default: false,
    },
})

const toast = useToast();
const emit = defineEmits(['scaned']);

const scanDialogVisible = ref(false)
const resultDialogVisible = ref(false)
const cameraLoading = ref(true)
const paused = ref(false)
const formData = ref({})
const scannedData = ref({})
const result = ref('')

const templates = {
    "badge": "badge_number$show_code$first_name$last_name$company"
}

function cameraLoaded() {
    cameraLoading.value = false
}

function openScanDialog() {
    result.value = ''
    scannedData.value = {}
    scanDialogVisible.value = true
    cameraLoading.value = true
    paused.value = false
}

function closeScanDialog(cameraOn = true) {
    result.value = ''
    scannedData.value = {}
    resultDialogVisible.value = false
    cameraLoading.value = false
    paused.value = false
    scanDialogVisible.value = cameraOn
}

function handleDetection(decoded) {
    const delimiter = detectDelimiter(templates[props.template]);
    const templateKeys = templates[props.template].split(delimiter);
    const decodedValue = decoded.map((code) => code.rawValue)[0];
    const decodedValues = decodedValue.split(delimiter);
    scannedData.value = templateKeys.reduce((acc, key, i) => {
        acc[key] = decodedValues[i] || '';
        return acc;
    }, {});

    paused.value = true
    setTimeout(() => {
        paused.value = false
        if (props.autoSubmit) {
            submitResult();
        } else {
            resultDialogVisible.value = true;
        }
    }, 1000);
}

function submitResult() {
    emit('scaned', scannedData.value);
    closeScanDialog(false);
}

function handleCameraError(err) {
    alert('Camera access error. Please allow access and try again.')
    scanDialogVisible.value = false
    cameraLoading.value = false
}

function showToast(title, message, severity) {
    toast.add({ severity, summary: title, detail: message, life: 3000 })
}

// Helper function to detect the most common delimiter in the template string
function detectDelimiter(template) {
    const delimiters = ['$', ':', ';', ',', '|', '&'];
    // Create a frequency map for each delimiter
    const delimiterFrequency = delimiters.reduce((acc, delimiter) => {
        const count = (template.match(new RegExp(delimiter, 'g')) || []).length;
        acc[delimiter] = count;
        return acc;
    }, {});
    // Special case: If the template contains a '$' (the most common case for QR data)
    if (delimiterFrequency['$'] > 0) {
        return '$';  // Prioritize '$' as a delimiter if it's present
    }
    // Otherwise, find the delimiter with the highest frequency
    const detectedDelimiter = Object.entries(delimiterFrequency).reduce((max, [key, value]) => {
        return value > max[1] ? [key, value] : max;
    }, ['', 0])[0]; // Return the delimiter with the highest frequency
    // If no delimiter is found, fallback to using '$'
    return detectedDelimiter || '$';
}

// Helper function to extract values from the decoded string based on the detected delimiter
function extractDecodedValues(decodedValue, delimiter) {
    // Split decoded value by the detected delimiter
    return decodedValue.split(delimiter);
}
</script>

<style scoped>
.dialog-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 80vw;
    max-width: 420px;
    min-width: 320px;
    height: 50vh;
}

.p-dialog-sm .p-card {
    width: 100%;
    height: 100%;
    padding: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.p-card .p-card-body,
.p-card .p-card-footer {
    padding: 1rem;
}

.qr-camera {
    height: 45vh;
    width: 100%;
    aspect-ratio: 1;
    overflow: hidden;
}

.qr-result {
    max-height: 320px;
}

.loading-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
}

.loading-indicator {
    font-weight: bold;
    font-size: 2rem;
    text-align: center;
    color: #000;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.validation-success,
.validation-failure,
.validation-pending {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.8);
    padding: 10px;
    text-align: center;
    font-weight: bold;
    font-size: 1.4rem;
    color: black;
    display: flex;
    flex-flow: column nowrap;
    justify-content: center;
}

.validation-success {
    color: green;
}

.validation-failure {
    color: red;
}

.validation-pending {
    color: orange;
}
</style>
